import React, { useState, useMemo } from 'react';
import { fc, MOS, getAllCats, getAllBills, mk } from '../utils';

const DATE_RANGES = [
  { k: 'all', l: 'All time' },
  { k: 'ytd', l: 'This year' },
  { k: '6mo', l: '6 months' },
  { k: '3mo', l: '3 months' },
];

const SEARCH_TYPES = [
  { k: 'spending', l: '🛒 Spending' },
  { k: 'income', l: '💰 Income' },
  { k: 'bills', l: '📋 Bills' },
  { k: 'savings', l: '💵 Savings' },
];

export default function Search({ state }) {
  const [query, setQuery] = useState('');
  const [selCat, setSelCat] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [groupByPlace, setGroupByPlace] = useState(false);
  const [searchType, setSearchType] = useState('spending');

  const { varSpend, customCats, disabledCats, incomeLog = [], fixedBills = [], cards = [], subscriptions = [], savDeposits = {} } = state;
  const cats = getAllCats(customCats, disabledCats);
  const allBills = getAllBills(cards, fixedBills, subscriptions);

  // Date cutoff key (YYYY-MM format for comparison)
  const cutoff = useMemo(() => {
    if (dateRange === 'all') return null;
    const d = new Date();
    if (dateRange === '3mo') d.setMonth(d.getMonth() - 3);
    if (dateRange === '6mo') d.setMonth(d.getMonth() - 6);
    if (dateRange === 'ytd') {
      d.setMonth(0);
      d.setDate(1);
    }
    return mk(d.getFullYear(), d.getMonth());
  }, [dateRange]);

  // All variable spending entries, flattened and sorted newest first
  const allEntries = useMemo(() => {
    const found = [];
    const cutoffNum = cutoff ? (() => { const [y, m] = cutoff.split('-').map(Number); return y * 12 + m; })() : null;
    Object.entries(varSpend || {}).forEach(([key, monthData]) => {
      const [year, month] = key.split('-').map(Number);
      // Use numeric comparison — string comparison breaks for months 10–11 ("2025-10" < "2025-5")
      if (cutoffNum !== null && year * 12 + month < cutoffNum) return;
      const label = MOS[month] + " '" + String(year).slice(2);
      Object.entries(monthData || {}).forEach(([catId, items]) => {
        const cat = cats.find((c) => c.id === catId);
        (items || []).forEach((item) => {
          found.push({ key, label, catId, cat, item, year, month, ts: item.ts || 0 });
        });
      });
    });
    return found.sort((a, b) => b.ts - a.ts || b.year - a.year || b.month - a.month);
  }, [varSpend, cats, cutoff]);

  // Filtered + sorted results
  const results = useMemo(() => {
    let list = allEntries;
    if (query.length >= 2) {
      const q = query.toLowerCase();
      list = list.filter((r) => r.item.place?.toLowerCase().includes(q));
    }
    if (selCat) list = list.filter((r) => r.catId === selCat);
    if (sortBy === 'amount') list = [...list].sort((a, b) => b.item.amt - a.item.amt);
    return list;
  }, [allEntries, query, selCat, sortBy]);

  // Grouped by place (aggregate same place across months)
  const grouped = useMemo(() => {
    const map = {};
    results.forEach((r) => {
      const key = (r.item.place || '').toLowerCase().trim();
      if (!map[key])
        map[key] = {
          place: r.item.place,
          total: 0,
          count: 0,
          cats: new Set(),
          lastLabel: r.label,
          em: r.cat?.em || '🏪',
          col: r.cat?.col,
        };
      map[key].total += r.item.amt;
      map[key].count++;
      if (r.cat?.name) map[key].cats.add(r.cat.name);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [results]);

  // Browse: spending by category (bar chart)
  const catTotals = useMemo(() => {
    const map = {};
    allEntries.forEach((r) => {
      if (!map[r.catId]) map[r.catId] = { cat: r.cat, catId: r.catId, total: 0, count: 0 };
      map[r.catId].total += r.item.amt;
      map[r.catId].count++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allEntries]);

  // Browse: top places by total spend
  const topPlaces = useMemo(() => {
    const map = {};
    allEntries.forEach((r) => {
      const key = (r.item.place || '').toLowerCase().trim();
      if (!map[key])
        map[key] = {
          place: r.item.place,
          total: 0,
          count: 0,
          em: r.cat?.em || '🏪',
          col: r.cat?.col,
        };
      map[key].total += r.item.amt;
      map[key].count++;
    });
    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [allEntries]);

  // Income search entries
  const incomeEntries = useMemo(() => {
    const q = query.toLowerCase();
    return (incomeLog || [])
      .map((e) => ({ ...e, dateObj: new Date(e.date + 'T12:00:00') }))
      .filter((e) => {
        if (!e.date) return false;
        if (cutoff) {
          const [cy, cm] = cutoff.split('-').map(Number);
          const [ey, em] = e.date.split('-').map(Number);
          if (ey * 12 + (em - 1) < cy * 12 + cm) return false;
        }
        if (q.length >= 2) return String(e.amt).includes(q) || (e.date || '').includes(q);
        return true;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [incomeLog, query, cutoff]);

  // Bills search entries (from all bills + paid history)
  const billEntries = useMemo(() => {
    const q = query.toLowerCase();
    return allBills.filter((b) => !q || b.name.toLowerCase().includes(q));
  }, [allBills, query]);

  // Savings deposits search
  const savingsEntries = useMemo(() => {
    const q = query.toLowerCase();
    const found = [];
    Object.entries(savDeposits || {}).forEach(([moKey, deps]) => {
      const [year, month] = moKey.split('-').map(Number);
      if (cutoff) {
        const [cy, cm] = cutoff.split('-').map(Number);
        if (year * 12 + month < cy * 12 + cm) return;
      }
      const label = MOS[month] + " '" + String(year).slice(2);
      (deps || []).forEach((d) => {
        if (!q || String(d.amt).includes(q)) {
          found.push({ ...d, moKey, label, year, month });
        }
      });
    });
    return found.sort((a, b) => b.ts - a.ts);
  }, [savDeposits, query, cutoff]);

  const total = results.reduce((s, r) => s + r.item.amt, 0);
  const isFiltered = query.length >= 2 || !!selCat;
  const showBrowse = !isFiltered && searchType === 'spending';
  const maxCat = catTotals.length ? Math.max(...catTotals.map((c) => c.total)) : 1;

  const exportCSV = () => {
    const rows = [['Date', 'Category', 'Place', 'Amount']];
    // Use allEntries (full history, respects date range filter)
    allEntries.forEach((r) => {
      const d = r.ts ? new Date(r.ts) : null;
      const dateStr = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : r.label;
      rows.push([dateStr, r.cat?.name || r.catId, r.item.place || '', r.item.amt.toFixed(2)]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spending-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Search + Filters */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="st" style={{ marginBottom: 0 }}>🔍 Search</div>
          {allEntries.length > 0 && (
            <button onClick={exportCSV} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--mut)', cursor: 'pointer' }}>
              ⬇ Export CSV
            </button>
          )}
        </div>

        {/* Type tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
          {SEARCH_TYPES.map((t) => (
            <button
              key={t.k}
              onClick={() => { setSearchType(t.k); setQuery(''); setSelCat(null); }}
              style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${searchType === t.k ? 'var(--ylw)' : 'var(--bdr)'}`, background: searchType === t.k ? 'rgba(255,217,61,.12)' : 'none', color: searchType === t.k ? 'var(--ylw)' : 'var(--mut)' }}
            >
              {t.l}
            </button>
          ))}
        </div>

        <input
          className="ob-inp"
          placeholder={searchType === 'spending' ? 'Search by place name…' : searchType === 'income' ? 'Search by amount or date…' : searchType === 'bills' ? 'Search by bill name…' : 'Search by amount…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          style={{ marginBottom: 10 }}
        />

        {/* Date range row */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
          {DATE_RANGES.map((d) => (
            <button
              key={d.k}
              onClick={() => setDateRange(d.k)}
              style={{
                padding: '3px 9px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${dateRange === d.k ? 'var(--ylw)' : 'var(--bdr)'}`,
                background: dateRange === d.k ? 'rgba(255,217,61,.12)' : 'none',
                color: dateRange === d.k ? 'var(--ylw)' : 'var(--mut)',
              }}
            >
              {d.l}
            </button>
          ))}
          {searchType === 'spending' && <div style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
            <button
              onClick={() => setSortBy((s) => (s === 'date' ? 'amount' : 'date'))}
              style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)' }}
            >
              {sortBy === 'date' ? '📅 Newest' : '💰 Amount'}
            </button>
            <button
              onClick={() => setGroupByPlace((g) => !g)}
              style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${groupByPlace ? 'var(--ylw)' : 'var(--bdr)'}`, background: groupByPlace ? 'rgba(255,217,61,.12)' : 'none', color: groupByPlace ? 'var(--ylw)' : 'var(--mut)' }}
            >
              Group
            </button>
          </div>}
        </div>

        {/* Category filter pills — spending only */}
        {searchType === 'spending' && <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: isFiltered ? 8 : 0 }}>
          <button
            onClick={() => setSelCat(null)}
            style={{
              padding: '3px 9px',
              borderRadius: 20,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              border: `1px solid ${!selCat ? 'var(--grn)' : 'var(--bdr)'}`,
              background: !selCat ? 'rgba(107,203,119,.12)' : 'none',
              color: !selCat ? 'var(--grn)' : 'var(--mut)',
            }}
          >
            All
          </button>
          {cats.map((c) => {
            const on = selCat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setSelCat(on ? null : c.id)}
                style={{
                  padding: '3px 9px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${on ? c.col || 'var(--grn)' : 'var(--bdr)'}`,
                  background: on ? `${c.col || 'var(--grn)'}22` : 'none',
                  color: on ? c.col || 'var(--grn)' : 'var(--mut)',
                }}
              >
                {c.em} {c.name}
              </button>
            );
          })}
        </div>}

        {/* Result summary + clear — spending only */}
        {searchType === 'spending' && isFiltered && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 11,
              color: 'var(--mut)',
            }}
          >
            <span>
              {results.length} result{results.length !== 1 ? 's' : ''}
            </span>
            {results.length > 0 && (
              <>
                <span>·</span>
                <strong style={{ color: 'var(--txt)' }}>{fc(total)} total</strong>
              </>
            )}
            <button
              onClick={() => {
                setQuery('');
                setSelCat(null);
              }}
              style={{
                marginLeft: 4,
                fontSize: 10,
                padding: '1px 7px',
                borderRadius: 5,
                border: '1px solid var(--bdr)',
                background: 'none',
                color: 'var(--mut)',
                cursor: 'pointer',
              }}
            >
              ✕ Clear
            </button>
          </div>
        )}
      </div>

      {/* ── BROWSE MODE ── */}
      {showBrowse && (
        <>
          {/* Spending by category */}
          {catTotals.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <div className="st" style={{ marginBottom: 0 }}>
                  📊 By Category
                </div>
                <span style={{ fontSize: 10, color: 'var(--mut)' }}>
                  {DATE_RANGES.find((d) => d.k === dateRange)?.l} · tap to filter
                </span>
              </div>
              <div style={{ marginTop: 10 }}>
                {catTotals.map((ct) => (
                  <div
                    key={ct.catId}
                    onClick={() => setSelCat(ct.catId)}
                    style={{ marginBottom: 10, cursor: 'pointer' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 3,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {ct.cat?.em} {ct.cat?.name || ct.catId}
                      </span>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                        <span style={{ fontSize: 10, color: 'var(--mut)' }}>
                          {ct.count} purchase{ct.count !== 1 ? 's' : ''}
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: ct.cat?.col || 'var(--txt)',
                          }}
                        >
                          {fc(ct.total)}
                        </span>
                      </div>
                    </div>
                    <div className="pw">
                      <div
                        className="pb"
                        style={{
                          background: ct.cat?.col || 'var(--ylw)',
                          width: Math.round((ct.total / maxCat) * 100) + '%',
                          opacity: 0.75,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top places */}
          {topPlaces.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <div className="st" style={{ marginBottom: 0 }}>
                  🏪 Top Places
                </div>
                <span style={{ fontSize: 10, color: 'var(--mut)' }}>tap to search</span>
              </div>
              {topPlaces.map((p, i) => (
                <div
                  key={i}
                  onClick={() => setQuery(p.place)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--bdr)',
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--mut)',
                      minWidth: 18,
                      textAlign: 'right',
                      fontWeight: 700,
                    }}
                  >
                    #{i + 1}
                  </span>
                  <span style={{ fontSize: 15, flexShrink: 0 }}>{p.em}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.place}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                      {p.count} purchase{p.count !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: p.col || 'var(--txt)',
                      flexShrink: 0,
                    }}
                  >
                    {fc(p.total)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {allEntries.length === 0 && (
            <div className="card cp">
              <div className="em">
                No spending logged yet — head to the 🧾 Spending tab to log expenses
              </div>
            </div>
          )}
        </>
      )}

      {/* ── SEARCH RESULTS ── */}
      {isFiltered && results.length > 0 && (
        <div className="card cp">
          {groupByPlace
            ? grouped.map((g, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 0',
                    borderBottom: '1px solid var(--bdr)',
                  }}
                >
                  <span style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>
                    {g.em}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {g.place}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                      {g.count} purchase{g.count !== 1 ? 's' : ''}
                      {g.cats.size > 0 && <> · {[...g.cats].join(', ')}</>}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: g.col || 'var(--txt)',
                      flexShrink: 0,
                    }}
                  >
                    {fc(g.total)}
                  </div>
                </div>
              ))
            : results.map((r, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 9,
                    padding: '9px 0',
                    borderBottom: '1px solid var(--bdr)',
                  }}
                >
                  <span style={{ fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0 }}>
                    {r.cat?.em || '📦'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.item.place}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                      {r.label} · {r.cat?.name || r.catId}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: r.cat?.col || 'var(--txt)',
                      flexShrink: 0,
                    }}
                  >
                    {fc(r.item.amt)}
                  </div>
                </div>
              ))}
        </div>
      )}

      {isFiltered && results.length === 0 && searchType === 'spending' && (
        <div className="card cp">
          <div className="em">No transactions found{query ? ` for "${query}"` : ''}</div>
        </div>
      )}

      {/* ── INCOME RESULTS ── */}
      {searchType === 'income' && (
        <div className="card cp">
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
            {incomeEntries.length} entr{incomeEntries.length !== 1 ? 'ies' : 'y'} · {fc(incomeEntries.reduce((s, e) => s + e.amt, 0))} total
          </div>
          {incomeEntries.length === 0 ? (
            <div className="em">No income logged yet</div>
          ) : incomeEntries.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{e.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>logged income</div>
              </div>
              <div style={{ fontWeight: 700, color: 'var(--grn)' }}>{fc(e.amt)}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── BILLS RESULTS ── */}
      {searchType === 'bills' && (
        <div className="card cp">
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
            {billEntries.length} bill{billEntries.length !== 1 ? 's' : ''} · {fc(billEntries.reduce((s, b) => s + b.amt, 0))}/mo
          </div>
          {billEntries.length === 0 ? (
            <div className="em">No bills found{query ? ` matching "${query}"` : ' — add them in 🗺️ Plan → Bills'}</div>
          ) : billEntries.map((b) => {
            return (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: b.col || 'var(--mut)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{b.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>Due the {b.due}{b.due === 1 ? 'st' : b.due === 2 ? 'nd' : b.due === 3 ? 'rd' : 'th'}</div>
                </div>
                <div style={{ fontWeight: 700, color: b.col || 'var(--txt)' }}>{fc(b.amt)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SAVINGS RESULTS ── */}
      {searchType === 'savings' && (
        <div className="card cp">
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
            {savingsEntries.length} deposit{savingsEntries.length !== 1 ? 's' : ''} · {fc(savingsEntries.reduce((s, d) => s + d.amt, 0))} total
          </div>
          {savingsEntries.length === 0 ? (
            <div className="em">No savings deposits logged yet</div>
          ) : savingsEntries.map((d, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{d.label}</div>
                <div style={{ fontSize: 10, color: 'var(--mut)' }}>{d.goalId ? 'allocated deposit' : 'general savings'}</div>
              </div>
              <div style={{ fontWeight: 700, color: 'var(--grn)' }}>{fc(d.amt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
